import {Button} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../../state/hooks';
import {PanelBankContextProvider} from '../../../state/panelbank/context';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {Ref as PanelBankSectionConfigRef} from '../../../state/views/panelBankSectionConfig/types';
import {useRunSelectorResizingContextMaybe} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import {usePanelBankSectionsScrollContext} from '../../PanelBank/PanelBankSectionsScrollContextProvider';
import {PanelCompRedux} from '../../PanelCompRedux';
import {useWorkspaceInheritedSettingsForPanel} from '../../WorkspaceDrawer/Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from '../hooks/hooks';
import {PanelContextProvider} from '../PanelContextProvider';
import {PanelEditorContainer} from '../PanelEditor/PanelEditorContainer';
import {PanelIDContextProvider} from '../PanelIDContext';
import {SharedPanelStateContextProvider} from '../SharedPanelStateContext';
import {useNextAndPreviousPanel} from '../useNextAndPreviousPanel';
import {usePanelViewTypeClass} from '../usePanelViewTypeClass';
import {FullScreenPanelHeader} from './FullScreenPanelHeader';
import {getPanelLinkById} from './fullScreenUtils';
import {useZoomStateFromURL} from './useZoomStateFromURL';

export const FullScreenPanelContainer = ({
  isProjectReadOnly,
  className,
  panelRef,
  panelBankSectionConfigRef,
}: {
  isProjectReadOnly: boolean; // whether user can edit project level resources (e.g. reports)
  className?: string;
  panelRef: PanelRef;
  panelBankSectionConfigRef: PanelBankSectionConfigRef;
}) => {
  const history = useHistory();

  // Note: ensure this component is wrapped in a SectionSettingsContextProvider
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const configXBounds = useConfigXBounds(panelRef);
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  // This ensures that we scroll to the correct section when user clicks
  // on the back button in the full screen panel.
  const panelBankSectionConfigId = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].__id__
  );
  const {scrollToStableSectionIdRef} = usePanelBankSectionsScrollContext();
  scrollToStableSectionIdRef.current = panelBankSectionConfigId;

  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  const isPanelAuto = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].isAuto
  );

  const panelViewTypeClass = usePanelViewTypeClass(panelType);

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [panelViewTypeClass, shouldRenderContent]);

  const {
    goToPreviousPanel,
    goToNextPanel,
    hasPreviousPanel,
    hasNextPanel,
    previousPanelTitle,
    nextPanelTitle,
  } = useNextAndPreviousPanel({panelBankSectionConfigRef, panelRef});

  // See note about full screen panels in SharedPanelStateContextProvider
  const zoomStateFromURL = useZoomStateFromURL();

  return (
    <PanelBankContextProvider>
      <PanelContextProvider
        isProjectReadOnly={isProjectReadOnly}
        panelBankSectionConfigRef={panelBankSectionConfigRef}
        panelRef={panelRef}
        inheritedSettings={inheritedSettings}>
        <PanelIDContextProvider stablePanelId={stablePanelId}>
          <SharedPanelStateContextProvider
            config={configXBounds}
            zoomState={zoomStateFromURL}>
            <PanelEditorContainer
              currentHeight={0} // TODO: make currentHeight default to 0 in component and make prop optional
              onOK={() => {
                // If panel is auto, then the current URL is based on the panel display name.
                // When a panel is edited, its display name may change (e.g. user updates
                // panel title or y axis metrics). We must redirect to the ID-based URL to
                // avoid ending up on a "panel not found" page. This is safe to do because
                // the panel will no longer be auto once it's edited.
                if (isPanelAuto) {
                  history.replace(getPanelLinkById(stablePanelId));
                }
              }}
            />
            <TailwindContents>
              <div
                className={twMerge(panelClassNames, 'px-24 py-12', className)}
                data-test="panel">
                <FullScreenPanelHeader
                  panelBankSectionConfigRef={panelBankSectionConfigRef}
                  panelType={panelType}
                />
                <div
                  className={twMerge(
                    'flex flex-col',
                    'h-full w-full grow-[1]',
                    'justify-center'
                  )}
                  id={'editable-panel-' + stablePanelId}>
                  <PanelCompRedux
                    configMode={false}
                    currentHeight={0}
                    dimensions={undefined as any}
                    panelRef={panelRef}
                  />
                </div>

                <div className="mt-24 flex">
                  <Button
                    startIcon="chevron-back"
                    onPress={e => {
                      goToPreviousPanel();
                      (e.target as HTMLElement).blur();
                    }}
                    isDisabled={!hasPreviousPanel}
                    variant="secondary"
                    tooltip={
                      hasPreviousPanel
                        ? {children: previousPanelTitle}
                        : undefined
                    }>
                    Previous
                  </Button>
                  <div className="ml-auto">
                    <Button
                      endIcon="chevron-next"
                      onPress={e => {
                        goToNextPanel();
                        (e.target as HTMLElement).blur();
                      }}
                      isDisabled={!hasNextPanel}
                      variant="secondary"
                      tooltip={
                        hasNextPanel ? {children: nextPanelTitle} : undefined
                      }>
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </TailwindContents>
          </SharedPanelStateContextProvider>
        </PanelIDContextProvider>
      </PanelContextProvider>
    </PanelBankContextProvider>
  );
};
